import { masterIdFromRef, masterNameFromRef } from "../../../uploads/productDraftApi";

/** Fields a person recognises, in the order the edit form shows them. Anything else is grouped as "Other details". */
const LABELS: Array<[string, string]> = [
  ["name", "Name"],
  ["color", "Colour"],
  ["description", "Description"],
  ["sellPrice", "Selling price"],
  ["costPrice", "Cost price"],
  ["artform", "Artform"],
  ["type", "Type"],
  ["category", "Category"],
  ["subcategory", "Sub-category"],
  ["weavetype", "Weave type"],
  ["tags", "Tags"],
  ["typeTagIds", "Type tags"],
  ["thumbnailDetails", "Photos"],
  ["flexibleUploadProcesses", "Making steps"],
  ["taxonomyValues", "Your attributes"],
];

const MONEY = new Set(["sellPrice", "costPrice"]);
const QUIET = new Set(["updatedAt", "updatedBy", "draftRevision", "publishedRevision", "_id", "id", "productUniqueId", "typeTagNames"]);

const show = (key: string, value: unknown): string => {
  if (value == null || value === "") return "—";
  if (MONEY.has(key)) {
    const n = Number(value);
    return Number.isFinite(n) ? `₹${n.toLocaleString("en-IN")}` : String(value);
  }
  if (key === "typeTagIds" && Array.isArray(value)) return `${value.length} tag${value.length === 1 ? "" : "s"}`;
  if (Array.isArray(value)) {
    if (value.every((v) => typeof v === "string")) return value.join(", ");
    return `${value.length} item${value.length === 1 ? "" : "s"}`;
  }
  if (typeof value === "object") return masterNameFromRef(value) || "changed";
  const text = String(value);
  return text.length > 60 ? `${text.slice(0, 57)}…` : text;
};

const MASTERS = new Set(["artform", "type", "category", "subcategory", "weavetype"]);

const norm = (key: string, v: unknown): unknown => {
  if (MASTERS.has(key)) return masterIdFromRef(v) || null;
  if (MONEY.has(key)) return v == null || v === "" ? null : Number(v);
  if (typeof v === "string") return v.trim().toLowerCase() || null;
  if (Array.isArray(v)) return v.length ? v : null;
  return v ?? null;
};

const same = (key: string, a: unknown, b: unknown) => JSON.stringify(norm(key, a)) === JSON.stringify(norm(key, b));

/**
 * What Publish changes will actually put live: the saved draft with the fields on screen laid over it
 * (Publish saves the screen first), so a price changed again after the last Save draft shows its new value.
 */
export const pendingDraftFromForm = (product: any, form: any): any => {
  const saved = product?.hasDraftChanges && product?.draft && typeof product.draft === "object" ? product.draft : {};
  const master = (v: any) => (masterIdFromRef(v) ? { _id: masterIdFromRef(v), name: masterNameFromRef(v) } : undefined);
  const onScreen: Record<string, unknown> = {
    name: form?.productName,
    color: form?.variantCode,
    description: form?.description,
    sellPrice: form?.sellPrice,
    costPrice: form?.costPrice,
    artform: master(form?.artform),
    type: master(form?.type),
    category: master(form?.category),
    subcategory: master(form?.subCategory),
    weavetype: master(form?.weaveType),
    tags: Array.isArray(form?.tags) ? form.tags : undefined,
    typeTagIds: Array.isArray(form?.typeTagIds) ? form.typeTagIds : undefined,
  };
  const draft: Record<string, unknown> = { ...saved };
  Object.entries(onScreen).forEach(([key, value]) => {
    if (value !== undefined) draft[key] = value;
  });
  return { ...product, hasDraftChanges: true, draft };
};

export type DraftChange = { key: string; label: string; from: string; to: string };

/** Live product vs its saved draft: what "Publish changes" will put live. */
export const draftChanges = (product: any): DraftChange[] => {
  const draft = product?.draft;
  if (!product?.hasDraftChanges || !draft || typeof draft !== "object") return [];
  const changed = Object.keys(draft).filter(
    (key) => !QUIET.has(key) && draft[key] !== null && draft[key] !== undefined && !same(key, draft[key], product[key])
  );
  const known = LABELS.filter(([key]) => changed.includes(key)).map(([key, label]) => ({
    key,
    label,
    from: show(key, product[key]),
    to: show(key, draft[key]),
  }));
  const others = changed.filter((key) => !LABELS.some(([k]) => k === key));
  return others.length
    ? [...known, { key: "other", label: "Other details", from: "", to: `${others.length} field${others.length === 1 ? "" : "s"} changed` }]
    : known;
};
